/* BookingTermsAcceptance — one shared component the booking flow drops
   onto the review step of ALL 11 templates (founder + 10 tenants).

   Renders the studio's booking policy inside a scrollable box, followed by
   an "I have read and agree" checkbox. The parent's `canForward` gate is
   already tied to the checkbox state via `useBookingFlow` (see
   hooks/useBookingFlow.js → canForward on final step requires
   termsAccepted). This component is purely presentational — it does NOT
   own state; the caller passes `checked` + `onChange`.

   Design notes:
   - Fallback policy: when `policyText` is empty (fresh tenant), we render
     a sensible generic policy so the customer STILL has something to accept
     and the checkbox retains legal meaning. Studios are expected to
     personalize this via /admin/site → Booking → Booking policy.
   - Styled to inherit the tenant CSS variables (`--tenant-fg`, `--tenant-accent`)
     so it matches whichever template it's dropped into without per-template
     forks. Falls back to sane defaults on the founder route.
   - Test IDs are stable so the testing agent can drive it uniformly across
     every template.
*/
import React from "react";

const FALLBACK_POLICY = `A non-refundable deposit is required to secure your session date. The balance is due before or on the day of the sitting. Reschedules require at least 48 hours' notice; late cancellations forfeit the deposit. Session images are delivered within the studio's standard turnaround window and remain the copyright of the studio unless a print/usage licence is separately agreed. By reserving a session you confirm you have read and accept these terms.`;

export default function BookingTermsAcceptance({
  policyText,
  checked,
  onChange,
  testidPrefix = "booking-terms",
}) {
  const text = (policyText && policyText.trim()) || FALLBACK_POLICY;
  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-block`}>
      <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55">
        Terms &amp; conditions
      </div>
      <div
        data-testid={`${testidPrefix}-policy`}
        className="max-h-40 overflow-y-auto border border-[color:var(--tenant-fg,#0E0D0C)]/15 bg-[color:var(--tenant-fg,#0E0D0C)]/5 p-4 text-[13px] leading-relaxed whitespace-pre-wrap text-[color:var(--tenant-fg,#0E0D0C)]/80"
      >
        {text}
      </div>
      <label className="flex items-start gap-3 cursor-pointer select-none pt-1">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange?.(e.target.checked)}
          data-testid={`${testidPrefix}-checkbox`}
          className="mt-0.5 h-4 w-4 accent-[color:var(--tenant-accent,#C9A961)] cursor-pointer"
        />
        <span className="text-[13px] text-[color:var(--tenant-fg,#0E0D0C)]/85">
          I have read and agree to the studio&rsquo;s terms &amp; conditions.
        </span>
      </label>
    </div>
  );
}
